import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeviceLogsService } from 'src/app/Core/services/device-logs.service';
import { ViewDialogComponent } from '../view-dialog/view-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { ToasterService } from 'src/app/Core/services/toaster.service';

@Component({
  selector: 'app-device-logs',
  templateUrl: './device-logs.component.html',
  styleUrls: ['./device-logs.component.css']
})
export class DeviceLogsComponent implements OnInit {
  enteredSerialNumber: string;
  enteredStartDate: Date | null = null;
  enteredEndDate: Date | null = null; 
  logs: any[] = []; 
  minDate: Date = new Date(2000, 0, 1);
  searchClicked: boolean = false;
  selectedLog: any;
  logContent: string;
  serialNumber: string;
  date: Date;
  dateFormat: string;
  sortColumn: string = 'date';     
  sortDirection: string = 'asc';   
  sortedLogs: any[] = [];  
  currentPage: number = 1;
  pageSize: number = 5;
  totalPages: number = 0;
  defaultPlaceholder: string = '5';
  deviceType: string | null = null;
  dateRangeError: string; 
  isDateRangeValid: boolean = false;
  serialNumberError: string;
  timezone: string;
  searchChanged: boolean = false;

  constructor(private deviceLogsService: DeviceLogsService, private dialog : MatDialog,private route: ActivatedRoute,private toaster: ToasterService) {}

  ngOnInit(): void {
    this.dateFormat = 'dd MMM YYYY h:mm:ss';
    this.timezone = this.getTimezone();
  }
  
 resetSearchFields(): void {
    this.enteredSerialNumber = '';
    this.enteredStartDate = null;
    this.enteredEndDate = null;
    this.logs = [];
    this.searchClicked = false;
    this.currentPage = 1;
    this.totalPages = 0;
    this.sortedLogs = [];
    this.dateRangeError = '';
  }

  searchLogs(): void {

    if (this.searchChanged) {
      this.currentPage = 1;
      this.searchChanged = false;
    }

    if (!this.validateSerialNumber()) {
      this.toaster.openDialogBox("Please enter a valid serial number.", false);
      return;
    }  
  
    if (this.isDateRangeValid) {
      const startDateStr = this.formatDate(this.enteredStartDate);
      const endDateStr = this.formatDate(this.enteredEndDate);

      this.deviceLogsService.getDeviceLogs(this.enteredSerialNumber, startDateStr, endDateStr).subscribe(
        (response) => {
          this.logs = response.logs || [];
          this.totalPages = Math.ceil(this.logs.length / this.pageSize);
          this.searchClicked = true;
          this.sortLogs();
          this.updatePaginatedLogs();
        },
        (error) => {
          console.error('Error fetching logs:', error);
          this.searchClicked = false;
          if(error.status === 403){
            this.toaster.openDialogBox("You are unauthorized to access this data",false);
            this.resetSearchFields();
            } else if (error.status === 400){
              // Check if the error message contains "unknown device type"
            if (error.error.message && error.error.message.includes('unknown device type')) {
              this.toaster.openDialogBox("Invalid Device type", false);
            }
          }
        }
      );
    } else {
      console.error('Please provide serial number and date range.');
    }
}

  validateDateRange(): void {
    if (this.enteredStartDate && this.enteredEndDate) {
      const dayDifference = this.calculateDayDifference(this.enteredStartDate, this.enteredEndDate);

      if (dayDifference > 31) {
        this.dateRangeError = "Logs can be fetched for a maximum of 31 days only.";
        this.isDateRangeValid = false;
      } else {
        this.dateRangeError = '';
        this.isDateRangeValid = true;
      }
      this.searchChanged = true;
    }
  }

  validateSerialNumber(): boolean {
    const serialNumberPattern = /^[0-9A-Za-z]{12,16}$/;
    const isValid = serialNumberPattern.test(this.enteredSerialNumber || '');
    this.serialNumberError = isValid ? '' : 'Serial number length must be 12 to 16 alphanumeric characters.';
    return isValid;
  }

  onSerialNumberInput(): void {
    this.validateSerialNumber();
    this.searchChanged = true;
  }

  calculateDayDifference(startDate: Date, endDate: Date): number {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)); 
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortLogs();
  }

  private sortLogs(): void {
    this.logs.sort((a, b) => {
      const valA = a[this.sortColumn];
      const valB = b[this.sortColumn];

      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  
    this.currentPage = 1;
    this.updatePaginatedLogs();
  }
  
  private updatePaginatedLogs(): void {
    const pageSizeNumber = Number(this.pageSize);
    const startIndex = (this.currentPage - 1) * pageSizeNumber;
    const endIndex = Math.min(startIndex + pageSizeNumber, this.logs.length);
  
    this.sortedLogs = [...this.logs.slice(startIndex, endIndex)];
  }

  private formatDate(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  viewLog(): void {
    if (!this.selectedLog) return;

    this.deviceLogsService.viewLogContent(this.selectedLog.file_path).subscribe(
      (response: any) => {
        this.logContent = response.content;
        this.serialNumber = response.serial_number;
        this.date = response.date;
        const parsedLogs = JSON.parse(this.logContent);
          const logCount = parsedLogs.length; 

        this.dialog.open(ViewDialogComponent, {
          data: { 
            serialnumber: this.serialNumber,
            date: this.date,
            type: this.logContent,
            count: logCount
           }
        });

      },(error) => {
        console.error('Error fetching log content:', error);
      }
    );
  }

  download(): void {
    if (!this.selectedLog) return;

    this.deviceLogsService.downloadLog(this.selectedLog.file_path).subscribe(
      (response: Blob) => {
        let fileName = this.selectedLog.file_path.split('/').pop();

        if (fileName) {
          fileName = fileName.replace(/\.[^/.]+$/, '') + '.txt';
        } else {
          fileName = 'logfile.txt';
        }
  
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      (error) => {
        console.error('Download failed', error);
      }
    );
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedLogs();
    }
  }
  
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedLogs();
    }
  }

  onPageSizeChange(event: any): void {
    this.pageSize = event.value;
    this.totalPages = Math.ceil(this.logs.length / this.pageSize);
    this.currentPage = 1;
    this.updatePaginatedLogs();
  }

  getTimezone(): string {
    const offset = new Date().getTimezoneOffset(); 
    const hours = Math.floor(Math.abs(offset) / 60); 
    const minutes = Math.abs(offset) % 60; 
    const sign = offset < 0 ? '+' : '-'; 

    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}
}